import React from 'react';
import * as R from 'ramda';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  KEYBOARD_SHORTCUTS,
  ShortcutAction,
  ShortcutKey,
} from 'constants/shortcuts';
import { getIsShortcutActionAllowedFunction } from 'reducers/cafaConfigs';
import { RootState } from 'reducers';

const groups = ['main', 'payment', 'general', 'settings'] as const;
type Group = typeof groups[number];
type Shortcut = {
  title: string;
  primary: ShortcutAction;
  secondary?: ShortcutAction;
};

const groupDict: {
  [key in Group]: {
    primaryKey: ShortcutAction;
    title: string;
    secondaryKey?: ShortcutAction;
  }[];
} = {
  main: [
    { primaryKey: 'openPayment', title: 'openPayment' },
    { primaryKey: 'newSale', title: 'newSale' },
    { primaryKey: 'searchProducts', title: 'searchProducts' },
    { primaryKey: 'searchCustomers', title: 'searchCustomers' },
  ],
  payment: [
    { primaryKey: 'fullCashPayment', title: 'exactCash' },
    { primaryKey: 'fullCardPayment', title: 'exactCard' },
    { primaryKey: 'enterCash', title: 'enterCash' },
    {
      primaryKey: 'confirmPayments',
      secondaryKey: 'confirm',
      title: 'confirmPayments',
    },
    {
      primaryKey: 'clearAmount',
      secondaryKey: 'clearAmountAlt',
      title: 'clearAmount',
    },
  ],
  general: [
    { primaryKey: 'cancel', title: 'closeCancel' },
    { primaryKey: 'confirm', title: 'confirm' },
  ],
  settings: [
    { primaryKey: 'collapseAll', title: 'collapseAll' },
    { primaryKey: 'expandAll', title: 'expandAll' },
  ],
};

const ShortcutText = ({ shortcut }: { shortcut: ShortcutKey }) => {
  if (typeof shortcut === 'string') {
    const textToShow = shortcut === 'Escape' ? 'Esc' : shortcut;
    return <kbd>{textToShow}</kbd>;
  }
  if (Array.isArray(shortcut)) {
    return R.pipe(
      R.map(key => <kbd key={key}>{key}</kbd>),
      R.intersperse(' + '),
    )(shortcut);
  }
  throw new Error(
    `Illegal params passed to shortcutText - expected string or string array, but received ${JSON.stringify(
      shortcut,
    )}`,
  );
};

const getShortcutsForGroup = (group: Group) => (state: RootState) => {
  const isShortcutActionAllowedFunc = getIsShortcutActionAllowedFunction(state);

  const shortcuts = groupDict[group];

  const result: Shortcut[] = [];
  shortcuts.forEach(shortcut => {
    const { primaryKey, secondaryKey, title } = shortcut;
    const sc = {} as Shortcut;

    if (isShortcutActionAllowedFunc(primaryKey)) {
      sc.primary = primaryKey;
      // Store both primary and secondary if present
      if (secondaryKey && isShortcutActionAllowedFunc(secondaryKey)) {
        sc.secondary = secondaryKey;
      }
    }
    // Store secondary as primary if primary missing
    else if (secondaryKey && isShortcutActionAllowedFunc(secondaryKey)) {
      sc.primary = secondaryKey;
    }

    if (Object.keys(sc).length) {
      result.push({ ...sc, title });
    }
  });

  return result;
};

const ShortcutGroup: React.FC<{ group: Group }> = ({ group }) => {
  const { t } = useTranslation('shortcuts', { keyPrefix: group });
  const shortcuts = useSelector(getShortcutsForGroup(group));

  if (!shortcuts.length) {
    return null;
  }

  return (
    <div className="window" key={group}>
      <h5>{t('title')}</h5>
      <ul>
        {shortcuts.map(sc => (
          <li key={sc.title}>
            <div>
              <span>{t(`${sc.title}`)}</span>
            </div>
            <div>
              <ShortcutText shortcut={KEYBOARD_SHORTCUTS[sc.primary]} />
              {sc.secondary ? (
                <>
                  <span>or</span>
                  <ShortcutText shortcut={KEYBOARD_SHORTCUTS[sc.secondary]} />
                </>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ShortcutsModal = () => {
  const newShortcuts = groups.map(g => <ShortcutGroup group={g} />);

  return <div className="shortcuts">{newShortcuts}</div>;
};

export default ShortcutsModal;
